import React from 'react'
import SettingsIcon from '@mui/icons-material/Settings'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import DeleteIcon from '@mui/icons-material/Delete'
import CachedIcon from '@mui/icons-material/Cached'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder'
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove'
import RefreshIcon from '@mui/icons-material/Refresh'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import { GenNode } from '../../types/carbon/models/GenNode'
import { getNodeID } from '../../utils/nodeTypes'
import type { MenuAction } from '../system/MenuActionsButton'
import { MULTI_TABLE_ID_META_KEY, MULTI_TABLE_FOLDER_META_KEY, MULTI_TABLE_FOLDER_META_FOLDER_VALUE } from '../../utils/multitables'
import { ALLOWED, decide, refuse, type ActionDecision, type WhoToAsk } from '../../utils/permissionReason'

// The actions in a Tables tree node's "More actions" menu, as data (#1534).
// The menu draws them, and the page's tools ask the same function whether an
// action is allowed for a node, so a tool can do only what the menu allows
// and refuses with the menu's own reason.

export type TocActionKey =
  | 'add_to_multi_table'
  | 'remove_from_multi_table'
  | 'context'
  | 'rename'
  | 'move_up'
  | 'move_down'
  | 'move'
  | 'duplicate'
  | 'make_copy'
  | 'delete'
  | 'new_folder'
  | 'download_folder_excel'
  | 'regenerate'
  | 'clear_folder_cache'
  | 'rebuild_folder_cache'
  | 'rebuild_all_cache'

export interface TocAction extends MenuAction {
  key: TocActionKey
}

export interface TocActionContext {
  isManager: boolean
  /** A Manager or a Realm admin: may clear and rebuild caches, as the server's isAdmin. */
  isAdmin: boolean
  /** The path is in the current user's My Tables. */
  isUserTable: (path: string) => boolean
  isInMultiTable: (path: string) => boolean
  /** The user has folders in My Tables, so Move has somewhere to go. */
  hasUserFolders: boolean
  /** Who the user asks about an action they cannot do (useWhoToAsk). */
  who: WhoToAsk
  on: {
    addToMultiTable: (node: GenNode) => void
    removeFromMultiTable: (path: string) => void
    context: (node: GenNode) => void
    rename: (node: GenNode) => void
    moveUp: (node: GenNode) => void
    moveDown: (node: GenNode) => void
    move: (node: GenNode) => void
    duplicate: (node: GenNode) => void
    /** Copy the table into the user's own My Tables, then open the copy. */
    makeCopy: (node: GenNode) => void
    delete: (node: GenNode) => void
    deleteMultiTable: (multiTableId: string) => void
    newFolder: (parent: GenNode) => void
    downloadFolderExcel: (node: GenNode) => void
    regenerate: (node: GenNode) => void
    clearFolderCache: (node: GenNode) => void
    rebuildFolderCache: (node: GenNode) => void
    rebuildAllCache: (node: GenNode) => void
  }
}

const icon = (C: React.ElementType, extra?: object) => <C fontSize="small" {...extra} />
const DANGER = '#990000'

/** The folder path an exec folder node stands for, relative to Tables/Exec. */
export function execFolderPath(node: GenNode): string {
  const folderName = node.value1 || ''
  const parentPath = (node.value2 || '').replace(/^Tables\/Exec\/?/, '')
  return parentPath ? `${parentPath}/${folderName}` : folderName
}

export function isSyntheticMultiTableFolder(node: GenNode): boolean {
  return !!node.meta?.some((m) => m.key === MULTI_TABLE_FOLDER_META_KEY && m.value === MULTI_TABLE_FOLDER_META_FOLDER_VALUE)
}

/** A colleague's My Tables root: a User node the server marks read-only. */
export function isColleagueRoot(node: GenNode): boolean {
  return node.type === 'User' && !!node.meta?.some((m) => m.key === 'readonly' && m.value === 'true')
}

/** The owner of a path under Tables/User/<email>/, else ''. */
export function tableOwner(path: string): string {
  const parts = path.replace(/\\/g, '/').split('/')
  return parts.length >= 3 && parts[0].toLowerCase() === 'tables' && parts[1].toLowerCase() === 'user' ? parts[2] : ''
}

// Where an item lives, which decides who may change it.
//   mine:      the user's own My Tables — everything.
//   shared:    Exec — a Manager changes it; anyone else asks their team manager.
//   colleague: another person's My Tables — nobody but its owner changes it
//              (a Manager may delete a table: DELETE /tables/.../data allows
//              the owner or a Manager since #1748).
type Place = 'mine' | 'shared' | 'colleague'

function placeOf(path: string, ctx: TocActionContext): Place {
  if (ctx.isUserTable(path)) return 'mine'
  return tableOwner(path) ? 'colleague' : 'shared'
}

/**
 * Every action the node's menu has, in menu order, each disabled with its
 * reason when this user cannot run it (Adam, 8 Oct: never hide an action,
 * say why and who to ask, and offer a copy where one exists). Empty: no menu.
 * The page tools read the same answer, so a tool refuses with the menu's
 * sentence.
 */
export function buildTocNodeActions(node: GenNode, ctx: TocActionContext): TocAction[] {
  const { on, who } = ctx
  // One action: allowed, or disabled with the reason `refuse` words.
  const act = (key: TocActionKey, title: string, Icon: React.ElementType, onClick: () => void, decision: ActionDecision = ALLOWED, color?: string): TocAction => ({
    key,
    title,
    icon: icon(Icon),
    onClick,
    ...(color ? { color } : {}),
    ...(decision.allowed ? {} : { disabled: true, reason: decision.reason }),
  })

  if (node.type === 'Table') {
    const fullPath = getNodeID(node)
    const place = placeOf(fullPath, ctx)
    const owner = place === 'colleague' ? tableOwner(fullPath) : undefined
    const thing = place === 'colleague' ? "a colleague's table" : 'this shared table'
    // Shared: a Manager may. A colleague's: nobody but its owner. Both offer
    // the copy, which sits in the same menu.
    const change = (verb: string, copy = true): ActionDecision =>
      decide(place === 'shared' && ctx.isManager, `You can't ${verb} ${thing}.`, who, { owner, copy })

    const actions: TocAction[] = [
      ctx.isInMultiTable(fullPath)
        ? act('remove_from_multi_table', 'Remove from multi-table', RemoveIcon, () => on.removeFromMultiTable(fullPath), ALLOWED, DANGER)
        : act('add_to_multi_table', 'Add to multi-table', AddIcon, () => on.addToMultiTable(node), ALLOWED, '#009900'),
      act('context', 'Context', SettingsIcon, () => on.context(node)),
    ]
    if (place === 'mine') {
      actions.push(
        act('rename', 'Rename', EditIcon, () => on.rename(node)),
        act('move_up', 'Move up', ArrowUpwardIcon, () => on.moveUp(node)),
        act('move_down', 'Move down', ArrowDownwardIcon, () => on.moveDown(node)),
        act('move', 'Move', DriveFileMoveIcon, () => on.move(node),
          ctx.hasUserFolders ? ALLOWED : { allowed: false, reason: 'There are no folders in My Tables to move it to.' }),
        act('duplicate', 'Duplicate', ContentCopyIcon, () => on.duplicate(node)),
        act('delete', 'Delete', DeleteIcon, () => on.delete(node), ALLOWED, DANGER),
      )
      return actions
    }
    actions.push(
      act('rename', 'Rename', EditIcon, () => on.rename(node), change('rename')),
      act('move', 'Move', DriveFileMoveIcon, () => on.move(node), change('move')),
      // Duplicate puts the copy beside the original; on a colleague's table
      // that is their My Tables, so it is never offered (Adam, 8 Oct).
      act('duplicate', 'Duplicate', ContentCopyIcon, () => on.duplicate(node), change('duplicate')),
      // Anyone copies a table they can read into their own My Tables (#1748).
      act('make_copy', 'Make your own copy', FileCopyIcon, () => on.makeCopy(node)),
      act('delete', 'Delete', DeleteIcon, () => on.delete(node),
        decide(ctx.isManager, `You can't delete ${thing}.`, who, { owner }), DANGER),
    )
    return actions
  }

  if (node.type === 'MultiTable') {
    const multiTableId = node.meta?.find((m) => m.key === MULTI_TABLE_ID_META_KEY)?.value || ''
    return [act('delete', 'Delete', DeleteIcon, () => on.deleteMultiTable(multiTableId), ALLOWED, DANGER)]
  }

  // Clearing and rebuilding caches: a Manager or a Realm admin, as the server.
  const cache = (cannot: string): ActionDecision => decide(ctx.isAdmin, cannot, who)

  if (node.type === 'Folder') {
    // No menu for the synthetic "My Table Lists" folder
    if (isSyntheticMultiTableFolder(node)) return []
    const place = placeOf(node.value2 || '', ctx)
    const owner = place === 'colleague' ? tableOwner(node.value2 || '') : undefined
    const thing = place === 'colleague' ? "a colleague's folder" : 'this shared folder'
    const change = (verb: string): ActionDecision => decide(place === 'shared' && ctx.isManager, `You can't ${verb} ${thing}.`, who, { owner })

    const actions: TocAction[] = []
    if (place === 'mine') {
      actions.push(
        // A My Tables folder nests: createTocFolder adds it to user.toc under this one.
        act('new_folder', 'New Subfolder', CreateNewFolderIcon, () => on.newFolder(node)),
        act('rename', 'Rename', EditIcon, () => on.rename(node)),
        act('move_up', 'Move up', ArrowUpwardIcon, () => on.moveUp(node)),
        act('move_down', 'Move down', ArrowDownwardIcon, () => on.moveDown(node)),
      )
    } else {
      // A shared folder takes a subfolder from a Manager; a colleague's shows it disabled, with the reason.
      actions.push(act('new_folder', 'New Subfolder', CreateNewFolderIcon, () => on.newFolder(node), change('make a folder in')))
      actions.push(act('rename', 'Rename', EditIcon, () => on.rename(node), change('rename')))
    }
    // "Download all to Excel" — a top-level folder takes in every table
    // beneath it, one sheet each, in TOC order (#1347 item 19b).
    actions.push(
      act('download_folder_excel', 'Download all to Excel', FileDownloadIcon, () => on.downloadFolderExcel(node)),
      act('regenerate', 'Regenerate tables', RefreshIcon, () => on.regenerate(node)),
      act('context', 'Context', SettingsIcon, () => on.context(node)),
      act('delete', 'Delete', DeleteIcon, () => on.delete(node), place === 'mine' ? ALLOWED : change('delete'), DANGER),
      act('clear_folder_cache', 'Clear Folder Cache', CachedIcon, () => on.clearFolderCache(node), cache("You can't clear this folder's cache.")),
      act('rebuild_folder_cache', 'Rebuild Folder Cache', RefreshIcon, () => on.rebuildFolderCache(node), cache("You can't rebuild this folder's cache.")),
    )
    return actions
  }

  if (node.type === 'User') {
    const colleague = isColleagueRoot(node)
    return [
      act('new_folder', 'New Folder', CreateNewFolderIcon, () => on.newFolder(node),
        colleague ? refuse("You can't make a folder in a colleague's My Tables.", who, { owner: node.value1 || undefined }) : ALLOWED),
      act('rebuild_all_cache', 'Rebuild All Cache', RefreshIcon, () => on.rebuildAllCache(node), cache("You can't rebuild the cache for these tables.")),
    ]
  }

  if (node.type === 'Section') {
    // The Exec root: top-level shared folders.
    return [act('new_folder', 'New Folder', CreateNewFolderIcon, () => on.newFolder(node),
      decide(ctx.isManager, "You can't make a folder in the shared tables.", who))]
  }

  return []
}

/** Whether the node's menu offers this action and the user may run it. */
export function tocActionOffered(node: GenNode, key: TocActionKey, ctx: TocActionContext): boolean {
  return buildTocNodeActions(node, ctx).some((a) => a.key === key && !a.disabled)
}

/** Why the user can't run this action on the node: the menu's reason, or undefined when they can (or the menu has no such action). */
export function tocActionRefusal(node: GenNode, key: TocActionKey, ctx: TocActionContext): string | undefined {
  return buildTocNodeActions(node, ctx).find((a) => a.key === key && a.disabled)?.reason
}
